import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// CRA (webpack 4) cannot consume the package's ESM main export; import the CJS
// dist bundle directly. If this import breaks after an SDK upgrade, check the new
// dist structure in node_modules/@workos-inc/authkit-react/.
import {
  AuthKitProvider,
  useAuth as useAuthKit,
  LoginRequiredError,
} from "@workos-inc/authkit-react/dist/index.js";
import axiosInstance from "../utils/axios";
import { setSession } from "../utils/jwt";
import { config } from "../constants";

const SessionContext = createContext({ accessToken: null });
const TenantsContext = createContext({ tenants: [] });

let authKitClient = null;
let signInInFlight = false;

function signOutUser() {
  setSession(null);
  if (authKitClient) {
    authKitClient.signOut({ returnTo: window.location.origin });
  } else {
    window.location.assign(window.location.origin);
  }
}

// Single-flight forced token refresh. WorkOS access tokens are short-lived
// (~5 min); a request can race a not-yet-completed background refresh and hit a
// momentarily-expired token. This lets the axios layer force a refresh and retry
// instead of logging the user out. Concurrent callers (several requests 401-ing
// at once) are coalesced into one refresh. On success the SDK's onRefresh
// callback (handleRefresh) already syncs the session via jwt.js setSession; we
// just return the fresh token so the caller can replay with it. Rejects with the
// SDK's LoginRequiredError when the session can no longer be refreshed.
let refreshPromise = null;
function getFreshAccessToken() {
  if (!authKitClient) {
    return Promise.reject(new Error("AuthKit client not initialized"));
  }
  if (!refreshPromise) {
    refreshPromise = authKitClient
      .getAccessToken({ forceRefresh: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Whether a forced-refresh rejection means the session is genuinely
// unrecoverable (the user must re-authenticate) rather than a transient or
// recoverable failure (a network blip, or a 401 that arrives before AuthKit has
// initialized). AuthKit raises LoginRequiredError only for terminal cases; the
// name fallback guards against instanceof failing across duplicate package
// copies. Only terminal failures should clear the session.
function isLoginRequiredError(error) {
  return (
    error instanceof LoginRequiredError ||
    (!!error && error.name === "LoginRequiredError")
  );
}

function ClientBridge({ children }) {
  const client = useAuthKit();

  useEffect(() => {
    authKitClient = client;
  }, [client]);

  return children;
}

function OrganizationGuard({ children }) {
  const { isLoading, user, organizationId } = useAuthKit();

  if (!isLoading && user && !organizationId) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: "12px",
          fontFamily: "sans-serif",
        }}
      >
        <span>
          No organization is linked to your account. Please contact
          support@datajolt.io.
        </span>
        <button type="button" onClick={signOutUser}>
          Sign out
        </button>
      </div>
    );
  }

  return children;
}

function TenantsProvider({ children }) {
  const { user } = useAuthKit();
  const { accessToken } = useContext(SessionContext);
  const [tenants, setTenants] = useState([]);
  // Track which user we fetched orgs for, so we re-fetch on re-login but not on
  // every token refresh (the org list depends on the user, not the token).
  const fetchedForUserRef = useRef(null);

  useEffect(() => {
    const uid = user?.id || user?.email || null;
    if (!accessToken || !uid) {
      fetchedForUserRef.current = null;
      return;
    }
    if (fetchedForUserRef.current === uid) return;
    fetchedForUserRef.current = uid;
    axiosInstance
      .get(`${config.url.WEBAPP}/api/user/organizations`)
      .then((response) => setTenants(response.data?.tenants || []))
      .catch((error) => {
        fetchedForUserRef.current = null;
        console.error("Error fetching organizations:", error);
      });
  }, [accessToken, user]);

  const value = useMemo(() => ({ tenants }), [tenants]);

  return (
    <TenantsContext.Provider value={value}>{children}</TenantsContext.Provider>
  );
}

function WorkOSAuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);

  const handleRefresh = useCallback((response) => {
    setSession(response.accessToken);
    setAccessToken(response.accessToken);
  }, []);

  const handleRefreshFailure = useCallback(({ signIn }) => {
    setSession(null);
    setAccessToken(null);
    signIn();
  }, []);

  const sessionValue = useMemo(() => ({ accessToken }), [accessToken]);

  return (
    <AuthKitProvider
      clientId={config.workos.CLIENT_ID}
      apiHostname={config.workos.AUTH_DOMAIN}
      devMode={!config.workos.AUTH_DOMAIN}
      redirectUri={window.location.origin}
      onRefresh={handleRefresh}
      onRefreshFailure={handleRefreshFailure}
    >
      <SessionContext.Provider value={sessionValue}>
        <ClientBridge>
          <OrganizationGuard>
            <TenantsProvider>{children}</TenantsProvider>
          </OrganizationGuard>
        </ClientBridge>
      </SessionContext.Provider>
    </AuthKitProvider>
  );
}

function useAuth() {
  const { isLoading, user: workosUser, organizationId } = useAuthKit();
  const { accessToken } = useContext(SessionContext);

  const user = useMemo(() => {
    if (!workosUser) return null;
    return {
      accessToken,
      tenantId: organizationId,
      email: workosUser.email,
      name:
        [workosUser.firstName, workosUser.lastName].filter(Boolean).join(" ") ||
        workosUser.email,
      profilePictureUrl: workosUser.profilePictureUrl,
    };
  }, [workosUser, organizationId, accessToken]);

  return { user, isAuthenticated: !isLoading && !!workosUser, isLoading };
}

function useTenantsState() {
  return useContext(TenantsContext);
}

function useAuthActions() {
  const { switchToOrganization } = useAuthKit();

  const switchTenant = useCallback(
    ({ tenantId }) => switchToOrganization({ organizationId: tenantId }),
    [switchToOrganization]
  );

  return { switchTenant };
}

function useLoginWithRedirect() {
  const { isLoading, user, signIn } = useAuthKit();
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current && !isLoading) {
      requestedRef.current = false;
      if (!user && !signInInFlight) {
        signInInFlight = true;
        // signIn() normally redirects (page unloads, latch resets on reload). Reset in
        // finally as well so the latch can never get stuck if it ever resolves in-page.
        signIn()
          .catch((error) => console.error("Error redirecting to login:", error))
          .finally(() => {
            signInInFlight = false;
          });
      }
    }
  }, [isLoading, user, signIn]);

  return useCallback(() => {
    requestedRef.current = true;
  }, []);
}

export {
  WorkOSAuthProvider,
  signOutUser,
  getFreshAccessToken,
  isLoginRequiredError,
  useAuth,
  useTenantsState,
  useAuthActions,
  useLoginWithRedirect,
};
